import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import RatingStats, { RatingStatsProps } from 'components/commercetools-ui/molecules/rating-stats';
import { ReviewList, ReviewListProps } from '../review-list';
import Typography from 'components/commercetools-ui/atoms/typography';
import RatingBanner from 'components/commercetools-ui/molecules/rating-banner';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import { useFormat } from 'helpers/hooks/useFormat';
import ReviewForm, { ReviewFormProps } from '../review-form';

export interface ReviewsProps {
  title?: string;
  titleHeadingLevel?: 'h1' | 'h2' | 'h3';
  ratingStatsProps: RatingStatsProps;
  reviewListProps: ReviewListProps;
  hasReviews?: boolean;
  rateBannerTitle?: string;
  rateBannerBody?: string;
  emptyBannerTitle?: string;
  emptyBannerBody?: string;
  reviewFormProps: ReviewFormProps;
  className?: string;
  // New props to control drawer externally
  onRateClick?: () => void;
  isDrawerOpen?: boolean;
  onClose?: () => void;
  // Custom banner button label
  bannerButtonLabel?: string;
}

const Reviews: React.FC<ReviewsProps> = ({
  title,
  titleHeadingLevel = 'h2',
  ratingStatsProps,
  reviewListProps,
  hasReviews = false,
  rateBannerTitle,
  rateBannerBody,
  emptyBannerTitle,
  emptyBannerBody,
  reviewFormProps,
  className = '',
  onRateClick,
  isDrawerOpen = false,
  onClose,
  bannerButtonLabel,
}) => {
  const { formatMessage } = useFormat({ name: 'common' });
  const [isConfirmation, setIsConfirmation] = useState(false);

  const handleClose = () => {
    // Reset confirmation state when closing the drawer
    setIsConfirmation(false);
    onClose?.();
  };

  const handleRateClick = () => {
    onRateClick?.();
  };

  const drawerContainerClass =
    'flex w-full h-screen overflow-y-auto px-0 items-end justify-center lg:items-center lg:justify-center';

  const formWrapperClass = 'relative w-full lg:max-w-[540px] bg-white p-32 pt-16 lg:pt-32 max-h-[90vh] overflow-y-auto';

  const drawerTitle = rateBannerTitle ?? formatMessage({ id: 'writeReview', defaultMessage: 'Write a review' });

  // Default button label if not provided
  const defaultButtonLabel = formatMessage({
    id: 'review.rate.button',
    defaultMessage: 'Write a Review',
  });

  const enhancedReviewFormProps = {
    ...reviewFormProps,
    onConfirmationChange: (isConfirming: boolean) => {
      setIsConfirmation(isConfirming);
    },
  };

  return (
    <div id="reviews" data-testid="reviews-container" className={`container pb-32 ${className}`}>
      {/* Title */}
      {hasReviews && title && (
        <Typography as={titleHeadingLevel} data-testid="reviews-title" className="body-text-2xlarge py-32 font-medium">
          {title}
        </Typography>
      )}

      {/* Content */}
      {!hasReviews ? (
        <div className="flex flex-col lg:flex-row lg:justify-between lg:gap-78">
          {title && (
            <Typography
              as={titleHeadingLevel}
              data-testid="reviews-title"
              className="body-text-2xlarge mb-32 font-medium lg:mb-0 lg:whitespace-nowrap"
            >
              {title}
            </Typography>
          )}
          <div className="w-full lg:w-[712px]">
            <RatingBanner
              data-testid="rating-banner"
              title={
                emptyBannerTitle ??
                formatMessage({
                  id: 'review.empty.title',
                  defaultMessage: 'No reviews yet, be the first one to leave a comment',
                })
              }
              body={
                emptyBannerBody ??
                formatMessage({ id: 'review.empty.subtitle', defaultMessage: 'Share your opinion with other buyers.' })
              }
              cta={{
                label: bannerButtonLabel || defaultButtonLabel,
                onClick: handleRateClick,
              }}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row lg:gap-96">
          <div data-testid="rating-stats" className="mb-24 block lg:hidden">
            <RatingStats {...ratingStatsProps} />
          </div>
          <div data-testid="review-list" className="mt-32 w-full lg:px-0">
            <ReviewList {...reviewListProps} />
          </div>
          <div data-testid="rate-section" className="block w-full lg:hidden">
            <RatingBanner
              data-testid="rating-banner"
              title={rateBannerTitle ?? formatMessage({ id: 'review.rate.title', defaultMessage: 'Rate this product' })}
              body={
                rateBannerBody ??
                formatMessage({ id: 'review.rate.subtitle', defaultMessage: 'Share your opinion with other buyers.' })
              }
              cta={{
                label: bannerButtonLabel || defaultButtonLabel,
                onClick: handleRateClick,
              }}
            />
          </div>
          <aside className="hidden lg:order-2 lg:flex lg:flex-col lg:gap-32">
            <div className="w-[440px] border border-stroke-primary px-32 py-24">
              <RatingStats {...ratingStatsProps} />
            </div>
            <RatingBanner
              data-testid="rating-banner"
              className="lg:flex-col"
              title={rateBannerTitle ?? formatMessage({ id: 'review.rate.title', defaultMessage: 'Rate this product' })}
              body={
                rateBannerBody ??
                formatMessage({ id: 'review.rate.subtitle', defaultMessage: 'Share your opinion with other buyers.' })
              }
              cta={{
                label: bannerButtonLabel || defaultButtonLabel,
                onClick: handleRateClick,
              }}
            />
          </aside>
        </div>
      )}

      <Drawer isOpen={isDrawerOpen} onClose={handleClose} direction="bottom" blockScrolling className="lg:inset-0">
        <div className={drawerContainerClass}>
          <div className={formWrapperClass}>
            <div className="flex flex-col">
              <div className="mb-24 flex items-center justify-between">
                {!isConfirmation && (
                  <Typography as="h2" className="body-text-large font-medium">
                    {drawerTitle}
                  </Typography>
                )}
                <XMarkIcon className="ml-auto size-24 cursor-pointer" onClick={handleClose} aria-label="Close Drawer" />
              </div>
              <div className="flex items-center justify-center">
                <ReviewForm {...enhancedReviewFormProps} />
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default Reviews;
